import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import BASE_URL from "../config";
import { Modal, Button } from "react-bootstrap"; // Import Bootstrap Modal
import { ToastContainer, toast } from "react-toastify"; // Import Toastify
import "react-toastify/dist/ReactToastify.css"; // Import Toastify CSS

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [usernames, setUsernames] = useState({});
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [showEditModal, setShowEditModal] = useState(false); // State to control modal visibility
  const [editingJob, setEditingJob] = useState(null); // State to track the job being edited
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem("token")) {
      window.location.href = "/login";
      return;
    }
  }, []);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${BASE_URL}api/jobs/all`, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        setJobs(response.data);
        fetchUsernames(response.data);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to fetch jobs", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const filteredJobs = jobs.filter((job) =>
    (job.title?.toLowerCase() || "").includes(search.toLowerCase()) ||
    (job.description?.toLowerCase() || "").includes(search.toLowerCase())
  );

  const sortedJobs = [...filteredJobs].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const valA = a[sortConfig.key]?.toString().toLowerCase() || "";
    const valB = b[sortConfig.key]?.toString().toLowerCase() || "";
    return sortConfig.direction === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
  });

  const totalPages = Math.ceil(sortedJobs.length / pageSize);
  const paginatedJobs = sortedJobs.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const deleteJob = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this job? This action cannot be undone.");
    if (!confirmDelete) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${BASE_URL}api/jobs/delete/${id}`, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      setJobs((prevJobs) => prevJobs.filter((job) => job.id !== id));
      toast.success("The job has been deleted successfully.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete job.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const fetchUsernames = async (jobs) => {
    const token = localStorage.getItem("token");
    const userIds = [
      ...new Set(
        jobs.flatMap((job) => [job.posted_by, job.modified_by]).filter(Boolean)
      ),
    ];

    const fetchedUsernames = {};
    await Promise.all(
      userIds.map(async (userId) => {
        try {
          const response = await axios.get(`${BASE_URL}api/find-one/${userId}`, {
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          });
          fetchedUsernames[userId] = response.data.username || "Unknown";
        } catch (error) {
          fetchedUsernames[userId] = "Unknown";
        }
      })
    );

    setUsernames(fetchedUsernames);
  };

  const handleEditClick = (job) => {
    setEditingJob({
      ...job,
      interview_date: job.interview_date ? job.interview_date.split("T")[0] : "", // Ensure interview_date is in YYYY-MM-DD format
    }); // Set the job being edited
    setShowEditModal(true); // Show the modal
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingJob((prev) => ({ ...prev, [name]: value })); // Update the editing job state
  };

  const saveChanges = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${BASE_URL}api/jobs/update/${editingJob.id}`, {
        status: editingJob.status,
        interview_date: editingJob.status === "Interview Scheduled" 
          ? editingJob.interview_date 
          : editingJob.interview_date || null, // Retain interview_date unless explicitly cleared
        notes: editingJob.notes,
      }, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      setJobs((prevJobs) =>
        prevJobs.map((job) =>
          job.id === editingJob.id
            ? {
                ...job,
                ...editingJob,
                interview_date: editingJob.status === "Interview Scheduled" 
                  ? editingJob.interview_date 
                  : job.interview_date, // Retain original interview_date if status is not "Interview Scheduled"
              }
            : job
        )
      );
      toast.success("The job has been updated successfully.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setShowEditModal(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update job.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  return (
    <div className="container mt-4">
      <div className="mb-3 d-flex justify-content-between">
        <h2>Jobs</h2>
        <a href="add-job" className="btn btn-primary">Add Job</a>
      </div>

      <div className="row mb-4">
        <div className="col-md-1">
          <select className="form-select" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
          </select>
        </div>
        <div className="col-md-6">
          <input type="text" className="form-control" placeholder="Search by title or description..." value={search} onChange={handleSearch} />
        </div>
      </div>

      {loading ? (
        <p>Loading jobs...</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover table-striped align-middle">
            <thead className="table-dark">
              <tr>
                <th onClick={() => requestSort("name")}>Name</th>
                <th onClick={() => requestSort("phone")}>Phone</th>
                <th onClick={() => requestSort("email")}>Email</th>
                <th onClick={() => requestSort("position")}>Position</th>
                <th onClick={() => requestSort("resume")}>Resume</th>
                <th onClick={() => requestSort("apply_date")}>Apply Date</th>
                <th onClick={() => requestSort("interview_date")}>Interview Date</th>
                <th onClick={() => requestSort("notes")}>Note</th>
                <th onClick={() => requestSort("status")}>Status</th>
                <th onClick={() => requestSort("")}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedJobs.length > 0 ? (
                paginatedJobs.map((job) => (
                  <tr key={job.id}>
                    <td>{job.name}</td>
                    <td>{job.phone || "N/A"}</td>
                    <td>{job.email || "N/A"}</td>
                    <td>{job.position || "N/A"}</td>
                    <td>
                      {job.resume_path ? (
                        <a href={job.resume_path} target="_blank" rel="noopener noreferrer">
                          View Resume
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td>{job.apply_date ? new Date(job.apply_date).toLocaleDateString("en-US") : "N/A"}</td>
                    <td>{job.interview_date ? new Date(job.interview_date).toLocaleDateString("en-US") : "N/A"}</td>
                    <td>{job.notes || "N/A"}</td>
                    <td>{job.status}</td>
                    <td>
                      <div className="d-flex justify-content-between">
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleEditClick(job)}
                        >
                          Edit
                        </button>
                        <button className="btn btn-sm btn-danger mx-2" onClick={() => deleteJob(job.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center">No jobs found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <div className="d-flex justify-content-between">
        <span>Showing {paginatedJobs.length} of {filteredJobs.length} Jobs</span>
        <div>
          <button
            className="btn btn-outline-primary mx-1"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </button>
          <span className="mx-2">Page {currentPage} of {totalPages}</span>
          <button
            className="btn btn-outline-primary mx-1"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </button>
        </div>
      </div>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Job</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingJob && (
            <form>
              <div className="mb-3">
                <label htmlFor="status" className="form-label">Status</label>
                <select
                  id="status"
                  name="status"
                  className="form-select"
                  value={editingJob.status || ""}
                  onChange={handleEditChange}
                >
                  <option value="Applied">Applied</option>
                  <option value="Shortlisted">Shortlisted</option>
                  <option value="Interview Scheduled">Interview Scheduled</option>
                  <option value="Interviewed">Interviewed</option>
                  <option value="Hired">Hired</option>
                  <option value="Rejected Before Interview">Rejected Before Interview</option>
                  <option value="Rejected After Interview">Rejected After Interview</option>
                </select>
              </div>
              {editingJob.status === "Interview Scheduled" && ( // Show interview date input only if status is "Interview Scheduled"
                <div className="mb-3">
                  <label htmlFor="interview_date" className="form-label">Interview Date</label>
                  <input
                    type="date"
                    id="interview_date"
                    name="interview_date"
                    className="form-control"
                    value={editingJob.interview_date || ""}
                    onChange={handleEditChange}
                  />
                </div>
              )}
              <div className="mb-3">
                <label htmlFor="notes" className="form-label">Notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  className="form-control"
                  rows="3"
                  value={editingJob.notes || ""}
                  onChange={handleEditChange}
                ></textarea>
              </div>
            </form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveChanges}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
      <ToastContainer /> {/* Add ToastContainer for notifications */}
    </div>
  );
};

export default Jobs;
